import React, { ReactNode } from 'react';
import { Button, Card, Row, Col, Form, Input, Tag, Space } from 'antd';
import { PlusOutlined, LinkOutlined, DisconnectOutlined, RollbackOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { FormInstance } from 'antd';

import ProductItemCard from '@components/shared/items/ProductItemCard';
import { useOperatorProductService } from '@hooks/operator/products/useOperatorProductService';

// Client-side BOM-link metadata stamped by ApplyBomButton on every applied row.
// See WorkOrderForm.handleValuesChange for the recalc / break-link rules.
interface BomSnapshot {
  product_id: string;
  product_sku?: string;
  product_name?: string;
  product_validity_days?: number | null;
  manufacturing_date_required?: boolean | null;
  batch_management_required?: boolean | null;
  inventory_type_id?: { value: string; label: string } | null;
  per_set_quantity?: number;
}

interface InventoryTypeOption {
  id: string;
  code?: string;
  name: string;
  [key: string]: unknown;
}

interface InventoryAttributeDefinition {
  id: string;
  name: string;
  label: string;
  data_type: string;
  required?: boolean;
  options?: unknown;
  [key: string]: unknown;
}

interface WorkOrderItemListProps {
  form: FormInstance;
  name: string;
  label: ReactNode;
  description?: ReactNode;
  merchantId: string;
  inventoryTypes: InventoryTypeOption[];
  inventoryAttributeDefinitions?: InventoryAttributeDefinition[];
  role: 'input' | 'output';
  /**
   * Optional slot rendered immediately to the right of the "+ 新增項目"
   * button. Used by the outputs instance to inject <ApplyBomButton>;
   * the inputs instance leaves it undefined so no BOM entry appears.
   */
  headerExtra?: ReactNode;
}

const VARIANT_STYLES = {
  output: {
    headerBackground: '#f6ffed',  // 淺綠色 - 表示「產出」
    headerBorder: '1px solid #b7eb8f',
  },
  input: {
    headerBackground: '#fff7e6',  // 淺橘色 - 表示「消耗」
    headerBorder: '1px solid #ffd591',
  },
} as const;

const WorkOrderItemList: React.FC<WorkOrderItemListProps> = ({
  form,
  name,
  label,
  description,
  merchantId,
  inventoryTypes,
  inventoryAttributeDefinitions = [],
  role,
  headerExtra,
}) => {
  const variantStyle = VARIANT_STYLES[role] || VARIANT_STYLES.input;
  const { t } = useTranslation();

  // 使用 Hook 自動注入 merchantId
  const productService = useOperatorProductService({ merchantId });

  // 創建給 ProductItemCard 使用的 service wrapper
  const searchProductsService = React.useMemo(() => ({
    fetchList: productService.fetchList,
  }), [productService]);

  const handleAdd = (add: (item: Record<string, unknown>) => void) => {
    const defaultInvType = inventoryTypes[0];
    add({
      role,
      product_id: null,
      quantity: 1,
      inventory_type_id: defaultInvType
        ? { value: defaultInvType.id, label: defaultInvType.name }
        : null,
      batch: '',
      expiration_date: null,
      note: '',
      custom_attributes: {},
    });
  };

  // Restore one row from its bom_snapshot and re-mark it linked. Called when
  // the user clicks the reset button on a broken row. The new quantity is
  // re-derived from per_set_quantity × current output[0].quantity so the row
  // is immediately back in formula-state — that's what makes subsequent output
  // qty edits start scaling this row again.
  const handleResetLink = (itemName: number) => {
    const items = (form.getFieldValue(name) || []) as Array<Record<string, unknown>>;
    const item = items[itemName];
    const snapshot = item?.bom_snapshot as BomSnapshot | undefined;
    if (!snapshot) return;

    let newQty = item.quantity as number;
    if (typeof snapshot.per_set_quantity === 'number') {
      const outputs = (form.getFieldValue('output_items') || []) as Array<{ quantity?: number }>;
      const outputQty = outputs[0]?.quantity ?? 1;
      newQty = snapshot.per_set_quantity * outputQty;
    }

    const updated = [...items];
    updated[itemName] = {
      ...item,
      product_id: snapshot.product_id,
      product_sku: snapshot.product_sku,
      product_name: snapshot.product_name,
      product_validity_days: snapshot.product_validity_days,
      manufacturing_date_required: snapshot.manufacturing_date_required,
      batch_management_required: snapshot.batch_management_required,
      inventory_type_id: snapshot.inventory_type_id ?? null,
      quantity: newQty,
      bom_linked: true,
    };
    form.setFieldsValue({ [name]: updated });
  };

  // 額外欄位：note 和 hidden role
  const renderExtraFields = (itemName: number, restField: Record<string, unknown>) => (
    <Row gutter={16}>
      {/* Hidden Role Field */}
      <Form.Item
        {...restField}
        name={[itemName, 'role']}
        hidden
      >
        <Input />
      </Form.Item>
      <Col span={24}>
        <Form.Item
          {...restField}
          name={[itemName, 'note']}
          label={t('models.work_order_item.note')}
        >
          <Input placeholder={t('common.placeholder.note')} />
        </Form.Item>
      </Col>
    </Row>
  );

  return (
    <Card
      title={
        <div>
          <div style={{ fontWeight: 600 }}>{label}</div>
          {description && (
            <div style={{ fontSize: 12, fontWeight: 400, color: '#666', marginTop: 4 }}>
              {description}
            </div>
          )}
        </div>
      }
      style={{ marginTop: 16 }}
      styles={{
        header: {
          background: variantStyle.headerBackground,
          border: variantStyle.headerBorder,
        },
      }}
    >
      <Form.List name={name}>
        {(fields, { add, remove }) => (
          <>
            <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
              <Button type="primary" onClick={() => handleAdd(add)} icon={<PlusOutlined />}>
                {t('common.add_item')}
              </Button>
              {headerExtra}
            </div>
            {fields.map(({ key, name: itemName, ...restField }) => (
              <BomLinkAwareRow
                key={key}
                form={form}
                listName={name}
                itemName={itemName}
                onResetLink={() => handleResetLink(itemName)}
              >
                <ProductItemCard
                  restField={restField}
                  name={itemName}
                  form={form}
                  onRemove={remove}
                  searchProductsService={searchProductsService}
                  inventoryTypes={inventoryTypes}
                  attributeDefinitions={inventoryAttributeDefinitions}
                  listName={name}
                  quantityFieldName="quantity"
                  quantityLabel={t('models.work_order_item.quantity')}
                  renderExtraFields={renderExtraFields}
                  alwaysShowBatchFields={true}
                  role="operator"
                />
              </BomLinkAwareRow>
            ))}
          </>
        )}
      </Form.List>
    </Card>
  );
};

/**
 * Wraps each ProductItemCard with a BOM-link badge above it.
 * - linked (bom_linked === true) → blue「BOM 連動中」tag
 * - broken (bom_snapshot exists but bom_linked === false) → orange tag +
 *   「重置為 BOM 預設」button (delegates to parent's onResetLink)
 * - no snapshot at all → no badge (row is a manually-added one)
 *
 * Uses Form.useWatch on the specific row so only this child re-renders when
 * its link state flips — avoids re-rendering all rows on every onValuesChange.
 */
interface BomLinkAwareRowProps {
  form: FormInstance;
  listName: string;
  itemName: number;
  onResetLink: () => void;
  children: ReactNode;
}

const BomLinkAwareRow: React.FC<BomLinkAwareRowProps> = ({
  form,
  listName,
  itemName,
  onResetLink,
  children,
}) => {
  const { t } = useTranslation();
  const linked = Form.useWatch([listName, itemName, 'bom_linked'], form);
  const snapshot = Form.useWatch([listName, itemName, 'bom_snapshot'], form) as BomSnapshot | undefined;

  const hasSnapshot = snapshot != null;
  const isLinked = linked === true;
  const isBroken = hasSnapshot && !isLinked;

  return (
    <div>
      {(isLinked || isBroken) && (
        <Space size={8} style={{ marginBottom: 4 }}>
          {isLinked && (
            <Tag color="blue" icon={<LinkOutlined />}>
              {t('shared.work_orders.apply_bom.linked_tag')}
            </Tag>
          )}
          {isBroken && (
            <>
              <Tag color="orange" icon={<DisconnectOutlined />}>
                {t('shared.work_orders.apply_bom.unlinked_tag')}
              </Tag>
              <Button
                size="small"
                type="link"
                icon={<RollbackOutlined />}
                onClick={onResetLink}
              >
                {t('shared.work_orders.apply_bom.reset_link')}
              </Button>
            </>
          )}
        </Space>
      )}
      {children}
    </div>
  );
};

export default WorkOrderItemList;
